import { CopyOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Collapse, Descriptions, Divider, Modal, Progress, Space, Steps, Table, Tag, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cancelProducerRun, getProducerRun } from '../api';
import { API_BASE_URL } from '../api/baseUrl';
import { openContentManagement } from '../pages/contentProduce/shared';
import { formatErrorMessage, resolveErrorMessage } from '../utils/errorMessage';

const { Text } = Typography;

const RUN_STATUS_META = {
  queued: { color: 'default', label: '排队中' },
  running: { color: 'processing', label: '运行中' },
  succeeded: { color: 'success', label: '成功' },
  failed: { color: 'error', label: '失败' },
  canceled: { color: 'warning', label: '已取消' },
  cancel_requested: { color: 'warning', label: '取消中' },
};

const PRODUCER_PHASES = {
  papers_to_notes: [
    { key: 'start', label: '开始' },
    { key: 'prepare_items', label: '准备数据' },
    { key: 'dedupe', label: '去重' },
    { key: 'convert', label: '解析/转换' },
    { key: 'llm', label: 'LLM 生成' },
    { key: 'images', label: '图片生成' },
    { key: 'upload', label: '上传资源' },
    { key: 'store', label: '写入内容库' },
    { key: 'done', label: '完成' },
  ],
  papers_to_wechat_article: [
    { key: 'start', label: '开始' },
    { key: 'prepare_items', label: '准备数据' },
    { key: 'dedupe', label: '去重' },
    { key: 'convert', label: '解析/转换' },
    { key: 'rewrite', label: '改写润色' },
    { key: 'render', label: '渲染排版' },
    { key: 'store', label: '写入内容库' },
    { key: 'done', label: '完成' },
  ],
  xhs_video_importer: [
    { key: 'start', label: '开始' },
    { key: 'collect_urls', label: '抓取链接' },
    { key: 'dedupe', label: '去重' },
    { key: 'download', label: '下载/上传' },
    { key: 'store', label: '写入内容库' },
    { key: 'done', label: '完成' },
  ],
  papers_to_video: [
    { key: 'start', label: '开始' },
    { key: 'prepare_items', label: '准备数据' },
    { key: 'dedupe', label: '去重' },
    { key: 'convert', label: '解析/转换' },
    { key: 'llm', label: '脚本生成' },
    { key: 'images', label: '图片生成' },
    { key: 'tts', label: '配音生成' },
    { key: 'render', label: '视频渲染' },
    { key: 'upload', label: '上传资源' },
    { key: 'store', label: '写入内容库' },
    { key: 'done', label: '完成' },
  ],
  weixin_channels_video_importer: [
    { key: 'start', label: '开始' },
    { key: 'wait_file', label: '等待下载' },
    { key: 'download', label: '下载' },
    { key: 'upload', label: '上传 OSS' },
    { key: 'store', label: '写入内容库' },
    { key: 'done', label: '完成' },
  ],
  video_bilingual_convert: [
    { key: 'start', label: '开始' },
    { key: 'download', label: '下载视频' },
    { key: 'audio', label: '抽取音频' },
    { key: 'subtitle', label: '提取字幕' },
    { key: 'asr', label: '语音识别' },
    { key: 'translate', label: '翻译' },
    { key: 'tts', label: '配音生成' },
    { key: 'demucs', label: '人声分离' },
    { key: 'mix', label: '混音' },
    { key: 'export', label: '合成视频' },
    { key: 'upload', label: '上传资源' },
    { key: 'done', label: '完成' },
  ],
  idea_to_video: [
    { key: 'start', label: '开始' },
    { key: 'idea', label: '创意优化' },
    { key: 'prepare_refs', label: '参考素材' },
    { key: 'video_model', label: '视频模型' },
    { key: 'audio', label: '音频处理' },
    { key: 'render', label: '本地兜底渲染' },
    { key: 'upload', label: '上传资源' },
    { key: 'store', label: '写入内容库' },
    { key: 'done', label: '完成' },
  ],
};

const clampPercent = (value) => {
  if (value === null || value === undefined) return undefined;
  const n = Number(value);
  if (!Number.isFinite(n)) return undefined;
  return Math.max(0, Math.min(100, Math.round(n)));
};

const computePercent = (progress, runStatus) => {
  const percent = clampPercent(progress?.percent);
  if (percent !== undefined) return percent;
  if (typeof progress?.current === 'number' && typeof progress?.total === 'number' && progress.total > 0) {
    return clampPercent((progress.current / progress.total) * 100);
  }
  if (runStatus === 'succeeded') return 100;
  return undefined;
};

const safeJsonParse = (text) => {
  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
};

const buildEventRow = (ev) => {
  const payload = ev?.payload || {};
  const messageText = payload?.message || formatErrorMessage(payload?.error_message) || payload?.error_message || '';
  return {
    key: String(ev?.seq || `${ev?.ts || ''}_${Math.random()}`),
    seq: ev?.seq,
    ts: ev?.ts,
    type: ev?.type,
    level: ev?.level,
    message: messageText,
    raw: ev,
  };
};

const ProducerRunViewer = ({
  runId,
  producerName,
  onStatusChange,
  onRunChange,
  onProgressChange,
  title = '运行进度',
}) => {
  const [runDoc, setRunDoc] = useState(null);
  const [runError, setRunError] = useState('');
  const [loading, setLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const [progress, setProgress] = useState(null);
  const [events, setEvents] = useState([]);
  const lastSeqRef = useRef(0);

  const pollingRef = useRef(null);
  const sseRef = useRef(null);

  const runStatus = runDoc?.status;
  const statusMeta = RUN_STATUS_META[runStatus] || { color: 'default', label: runStatus || '-' };
  const percent = computePercent(progress || runDoc?.progress, runStatus);

  const phaseSteps = useMemo(() => {
    const def = PRODUCER_PHASES[producerName] || [
      { key: 'start', label: '开始' },
      { key: 'done', label: '完成' },
    ];
    return def;
  }, [producerName]);

  const currentPhaseKey = (progress || runDoc?.progress)?.phase;
  const currentStepIndex = useMemo(() => {
    if (runStatus === 'succeeded') return phaseSteps.length; // mark all steps as finished
    const idx = currentPhaseKey ? phaseSteps.findIndex((s) => s.key === currentPhaseKey) : -1;
    if (idx >= 0) return idx;
    if (runStatus === 'failed' || runStatus === 'canceled') return Math.max(0, phaseSteps.length - 1);
    return 0;
  }, [currentPhaseKey, phaseSteps, runStatus]);

  const isTerminal = runStatus === 'succeeded' || runStatus === 'failed' || runStatus === 'canceled';
  const isCancelable = runStatus === 'queued' || runStatus === 'running';

  const refresh = useCallback(async () => {
    if (!runId) return;
    setLoading(true);
    setRunError('');
    try {
      const resp = await getProducerRun(runId);
      if (resp?.status === 'success') {
        setRunDoc(resp.run || null);
        setProgress(resp.run?.progress || null);
        if (typeof onStatusChange === 'function') onStatusChange(resp.run?.status);
        if (typeof onRunChange === 'function') onRunChange(resp.run || null);
        if (typeof onProgressChange === 'function') onProgressChange(resp.run?.progress || null);
      } else {
        setRunError(resolveErrorMessage(resp?.error_message, '获取运行状态失败'));
      }
    } catch (e) {
      setRunError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [onProgressChange, onRunChange, onStatusChange, runId]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollingRef.current = setInterval(() => {
      refresh();
    }, 2000);
  }, [refresh, stopPolling]);

  const stopSse = useCallback(() => {
    if (sseRef.current) {
      try {
        sseRef.current.close();
      } catch (_) {
        // ignore
      }
      sseRef.current = null;
    }
  }, []);

  const startSse = useCallback(() => {
    stopSse();
    if (!runId) return;

    const url = `${API_BASE_URL}/producers/runs/${encodeURIComponent(runId)}/events`;
    const es = new EventSource(url);
    sseRef.current = es;

    const onAnyEvent = (raw) => {
      const ev = safeJsonParse(raw?.data);
      if (!ev) return;
      if (typeof ev.seq === 'number') lastSeqRef.current = Math.max(lastSeqRef.current, ev.seq);

      if (ev.type === 'progress' && ev.payload) {
        setProgress(ev.payload);
        if (typeof onProgressChange === 'function') onProgressChange(ev.payload);
      }
      if (ev.type !== 'progress' && ev.type !== 'heartbeat') {
        setEvents((prev) => {
          const next = [...prev, buildEventRow(ev)];
          return next.length > 400 ? next.slice(next.length - 400) : next;
        });
      }
    };

    const onDone = (raw) => {
      onAnyEvent(raw);
      stopSse();
      stopPolling();
      refresh();
    };

    es.addEventListener('progress', onAnyEvent);
    es.addEventListener('log', onAnyEvent);
    es.addEventListener('error', onAnyEvent);
    es.addEventListener('heartbeat', () => {});
    es.addEventListener('done', onDone);

    es.onerror = () => {
      // SSE 可能被代理缓冲/断开；降级为轮询保证进度可见
      startPolling();
    };
  }, [onProgressChange, refresh, runId, startPolling, stopPolling, stopSse]);

  useEffect(() => {
    setRunDoc(null);
    setProgress(null);
    setEvents([]);
    lastSeqRef.current = 0;
    setRunError('');
    stopPolling();
    stopSse();

    if (!runId) return () => {};

    refresh();
    startSse();
    startPolling();

    return () => {
      stopPolling();
      stopSse();
    };
  }, [refresh, runId, startPolling, startSse, stopPolling, stopSse]);

  useEffect(() => {
    if (isTerminal) {
      stopPolling();
      stopSse();
    }
  }, [isTerminal, stopPolling, stopSse]);

  const handleCancel = async () => {
    if (!runId) return;
    Modal.confirm({
      title: '停止运行',
      content: '将停止当前正在运行的这一次生产（可能需要一点时间生效）。确定要停止吗？',
      okText: '停止运行',
      cancelText: '取消',
      onOk: async () => {
        setCanceling(true);
        try {
          const resp = await cancelProducerRun(runId);
          if (resp?.status === 'accepted') {
            message.success('已提交停止请求');
            setRunDoc((prev) => (prev ? { ...prev, status: 'cancel_requested' } : prev));
            if (typeof onStatusChange === 'function') onStatusChange('cancel_requested');
          } else {
            message.warning('停止请求未成功提交');
          }
        } catch (e) {
          message.error(e?.message || String(e));
        } finally {
          setCanceling(false);
        }
      },
    });
  };

  const created = runDoc?.result?.created_content_ids || [];
  const skipped = runDoc?.result?.skipped || [];
  const hasNoOutputWarning = runStatus === 'succeeded' && created.length === 0 && skipped.length > 0;

  const createdColumns = useMemo(() => ([
    {
      title: '内容ID',
      dataIndex: 'content_id',
      key: 'content_id',
      render: (value) => <Text code>{value}</Text>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 140,
      render: () => (
        <Button size="small" onClick={openContentManagement}>
          跳转内容管理
        </Button>
      ),
    },
  ]), []);

  const skippedColumns = useMemo(() => ([
    { title: '内容ID', dataIndex: 'content_id', key: 'content_id', render: (v) => (v ? <Text code>{v}</Text> : '-') },
    { title: '原因', dataIndex: 'reason', key: 'reason', render: (v) => (v ? <Text>{v}</Text> : '-') },
    { title: '外部ID', dataIndex: 'external_id', key: 'external_id', render: (v) => (v ? <Text code>{v}</Text> : '-') },
  ]), []);

  const eventColumns = useMemo(() => ([
    {
      title: '序号',
      dataIndex: 'seq',
      key: 'seq',
      width: 70,
      render: (v) => (v === null || v === undefined ? '-' : <Text code>{v}</Text>),
    },
    { title: '时间', dataIndex: 'ts', key: 'ts', width: 180, render: (v) => (v ? <Text>{v}</Text> : '-') },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (v) => <Text>{v}</Text>,
    },
    {
      title: '级别',
      dataIndex: 'level',
      key: 'level',
      width: 90,
      render: (v) => {
        const color = v === 'error' ? 'red' : (v === 'warning' ? 'orange' : 'blue');
        return <Tag color={color}>{v || 'info'}</Tag>;
      },
    },
    { title: '消息', dataIndex: 'message', key: 'message', render: (v) => <Text>{v || '-'}</Text> },
  ]), []);

  return (
    <Card
      title={title}
      size="small"
      style={{ height: '100%' }}
      extra={(
        <Space>
          {runId && (
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(runId);
                  message.success('已复制 run_id');
                } catch (_) {
                  message.warning('复制失败');
                }
              }}
            >
              复制 run_id
            </Button>
          )}
          {runId && (
            <Button size="small" icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
              刷新
            </Button>
          )}
          {runId && (
            <Button
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={handleCancel}
              loading={canceling}
              disabled={!isCancelable}
            >
              停止运行
            </Button>
          )}
        </Space>
      )}
    >
      {!runId && (
        <Alert
          type="info"
          showIcon
          message="尚未开始生产"
          description="提交后会在这里展示进度与结果。"
        />
      )}

      {runId && (
        <>
          {runError && <Alert type="warning" showIcon message="获取运行信息失败" description={runError} style={{ marginBottom: 12 }} />}

          <Descriptions size="small" column={1} style={{ marginBottom: 10 }}>
            <Descriptions.Item label="运行ID">
              <Text code>{runId}</Text>
            </Descriptions.Item>
            {runDoc?.task_name && (
              <Descriptions.Item label="任务名">
                <Text>{runDoc.task_name}</Text>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="状态">
              <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
              {runDoc?.cancel_requested && <Tag color="warning">取消中</Tag>}
            </Descriptions.Item>
            {(progress || runDoc?.progress)?.message && (
              <Descriptions.Item label="消息">
                <Text>{(progress || runDoc?.progress)?.message}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>

          <Progress
            percent={percent ?? 0}
            status={runStatus === 'failed' ? 'exception' : (runStatus === 'succeeded' ? 'success' : 'active')}
            showInfo
            format={(p) => (percent === undefined ? '运行中' : `${p}%`)}
          />

          <Divider style={{ margin: '10px 0' }} />

          <Steps
            size="small"
            direction="vertical"
            current={Math.max(0, currentStepIndex)}
            status={runStatus === 'failed' || runStatus === 'canceled' ? 'error' : 'process'}
            items={phaseSteps.map((s) => ({ key: s.key, title: s.label }))}
          />

          {hasNoOutputWarning && (
            <>
              <Divider style={{ margin: '10px 0' }} />
              <Alert
                type="warning"
                showIcon
                message="运行已完成，但没有产出内容"
                description="created=0，详情请查看 skipped 列表（常见原因：PDF→Markdown 转换失败或依赖/文件缺失）。"
              />
            </>
          )}

          <Divider style={{ margin: '10px 0' }} />

          <Collapse
            items={[
              {
                key: 'logs',
                label: `运行日志（${events.length}）`,
                children: (
                  <Table
                    size="small"
                    columns={eventColumns}
                    dataSource={events}
                    pagination={false}
                    locale={{ emptyText: '暂无日志' }}
                    scroll={{ x: true }}
                  />
                ),
              },
            ]}
          />

          {runDoc?.error && (
            <>
              <Divider style={{ margin: '10px 0' }} />
              <Alert
                type="error"
                showIcon
                message="运行失败"
                description={formatErrorMessage(runDoc?.error?.error_message) || '未知错误'}
              />
            </>
          )}

          {runDoc?.result && (
            <>
              <Divider style={{ margin: '10px 0' }} />

              <Space size="middle" wrap style={{ marginBottom: 8 }}>
                <Text>
                  已创建: <Text strong>{created.length}</Text>
                </Text>
                <Text>
                  已跳过: <Text strong>{skipped.length}</Text>
                </Text>
              </Space>

              <Text strong>已创建内容</Text>
              <Table
                size="small"
                rowKey="content_id"
                pagination={false}
                columns={createdColumns}
                dataSource={created.map((id) => ({ content_id: id }))}
                locale={{ emptyText: '暂无' }}
                style={{ marginTop: 8, marginBottom: 10 }}
              />

              <Text strong>跳过列表</Text>
              <Table
                size="small"
                rowKey={(row, idx) => `${row.content_id || 'noid'}_${idx}`}
                pagination={false}
                columns={skippedColumns}
                dataSource={skipped}
                locale={{ emptyText: '暂无' }}
                style={{ marginTop: 8, marginBottom: 10 }}
              />

              <Text strong>统计</Text>
              <pre style={{ margin: 0, marginTop: 8, whiteSpace: 'pre-wrap' }}>
                {runDoc?.result?.stats ? JSON.stringify(runDoc.result.stats, null, 2) : '暂无'}
              </pre>
            </>
          )}
        </>
      )}
    </Card>
  );
};

export default ProducerRunViewer;
